/* [ START OF MODULE - VER 4.14 (src/App.jsx) ] */
import React, { useState, useEffect, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';

// Providers
import { ThemeProvider } from './context/ThemeContext.jsx';
import { useTosGate } from './hooks/useTosGate';
import TosGate from './components/TosGate'; 
import { SyncProvider } from './context/SyncContext';
import { RealtimeProvider } from './context/RealtimeContext';
import { AuthProvider } from './context/AuthContext';
import { useAuth } from './context/useAuth';
import { AppDataProvider } from './context/AppDataContext'; 

// TigerGate loads immediately — it IS the login screen, must be eager
import TigerGate from './TigerGate'; 
import SetupPassword from './pages/SetupPassword';
import { useIsMobile, isTabletDevice } from './hooks/useIsMobile';
import ErrorBoundary from './components/Shared/ErrorBoundary';

// Lazy chunk factories — assigned to consts so we can both render them
// and preload them on demand (see preloadPostAuthChunks below).
const loadCommandCenter         = () => import('./components/CommandCenter/CommandCenter');
const loadMobileMap             = () => import('./components/mobile/MobileMap');
const loadClientDashboardLayout = () => import('./components/ClientDashboard/ClientDashboardLayout');

const CommandCenter         = lazy(loadCommandCenter);
const MobileMap             = lazy(loadMobileMap);
const ClientDashboardLayout = lazy(loadClientDashboardLayout);
const ClientOverview        = lazy(() => import('./components/ClientDashboard/ClientOverview'));
const ClientIntakeForm      = lazy(() => import('./components/ClientDashboard/ClientIntakeForm'));
const ClientCMDB            = lazy(() => import('./components/ClientDashboard/ClientCMDB'));
const ClientMessages        = lazy(() => import('./components/ClientDashboard/ClientMessages'));
const ClientInitiatives     = lazy(() => import('./components/ClientDashboard/ClientInitiatives')); // RMI-2026-757
const MobileClientOverview  = lazy(() => import('./components/ClientDashboard/MobileClientOverview.jsx'));
const MobileClientIntakeForm= lazy(() => import('./components/ClientDashboard/MobileClientIntakeForm.jsx'));
const MobileClientCMDB      = lazy(() => import('./components/ClientDashboard/MobileClientCMDB.jsx'));
const MobileClientMessages  = lazy(() => import('./components/ClientDashboard/MobileClientMessages.jsx'));
const MobileShell           = lazy(() => import('./components/mobile/MobileShell.jsx'));
const ClientProfileEditor   = lazy(() => import('./components/ClientDashboard/ClientProfileEditor.jsx'));
const RfpFullScreen         = lazy(() => import('./components/RFP/RfpFullScreen'));
const AccountingApp         = lazy(() => import('./components/Accounting/AccountingApp'));

import './App.css';
import logo from './assets/Logo.png';

// Preload the post-auth route chunk in parallel with the auth/profile
// resolution. Without this, the user waits on a serial timeline:
//   auth resolve → profile fetch → THEN start downloading CommandCenter chunk.
// With preload kicked off as soon as we have a session, the chunk
// downloads in parallel with the profile fetch, often arriving before
// the user even sees the dashboard route.
let preloadFired = false;
const preloadPostAuthChunks = (isMobile, profileType) => {
  if (preloadFired) return;
  preloadFired = true;
  if (profileType === 'client') {
    loadClientDashboardLayout();
  } else if (isMobile) {
    loadMobileMap();
  } else {
    loadCommandCenter();
  }
};

// Branded auth loader — shown during isAuthLoading AND post-auth session resolution.
// Replaces the minimal bar loader so users never see TigerGate flash after login.
const AppLoader = ({ message = 'Initializing secure session...' }) => (
  <div style={{
    height: '100vh', width: '100vw', display: 'flex', flexDirection: 'column',
    alignItems: 'center', justifyContent: 'center',
    background: '#231F20', position: 'relative', overflow: 'hidden', fontFamily: "'Poppins', sans-serif"
  }}>
    <style>{`
      @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;600;700&display=swap');
      @keyframes pulse-slide { 0% { transform: translateX(-110%); } 100% { transform: translateX(410%); } }
      @keyframes loader-blob-drift { from { transform: translate(0,0) scale(1); } to { transform: translate(30px,20px) scale(1.08); } }
      @keyframes loader-ring-pulse { 0%,100% { box-shadow: 0 0 0 0 rgba(130,74,248,0); } 50% { box-shadow: 0 0 32px 8px rgba(130,74,248,0.45); } }
      @keyframes loader-fade-in { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: none; } }
      @keyframes loader-dot { 0%,80%,100% { opacity: 0.2; transform: scale(0.7); } 40% { opacity: 1; transform: scale(1); } }
    `}</style>

    {/* Ambient blobs */}
    <div style={{ position: 'absolute', inset: 0, zIndex: 0, overflow: 'hidden' }}>
      <div style={{ position: 'absolute', width: 480, height: 480, borderRadius: '50%', background: '#824AF8', opacity: 0.13, top: -140, left: -140, filter: 'blur(80px)', animation: 'loader-blob-drift 14s ease-in-out infinite alternate' }} />
      <div style={{ position: 'absolute', width: 380, height: 380, borderRadius: '50%', background: '#5899CD', opacity: 0.11, bottom: -100, right: -100, filter: 'blur(80px)', animation: 'loader-blob-drift 18s ease-in-out infinite alternate-reverse' }} />
      <div style={{ position: 'absolute', inset: 0, backgroundImage: 'radial-gradient(rgba(255,255,255,.04) 1px, transparent 1px)', backgroundSize: '32px 32px' }} />
    </div>

    {/* Logo + ring */}
    <div style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 24, animation: 'loader-fade-in 0.6s ease both' }}>
      <div style={{
        width: 80, height: 80, borderRadius: '50%', padding: 3,
        background: 'linear-gradient(135deg,#5899CD 0%,#824AF8 55%,#F37664 100%)',
        animation: 'loader-ring-pulse 2.4s ease-in-out infinite'
      }}>
        <div style={{ width: '100%', height: '100%', borderRadius: '50%', background: '#231F20', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
          <img src={logo} alt="Tiger Eye" style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} onError={e => { e.currentTarget.style.display='none'; e.currentTarget.parentNode.querySelector('.te-fallback') && (e.currentTarget.parentNode.querySelector('.te-fallback').style.display='flex'); }} />
          <div className="te-fallback" style={{ display: 'none', width: '100%', height: '100%', borderRadius: '50%', alignItems: 'center', justifyContent: 'center', background: '#231F20' }}><span style={{ color:'#fff', fontWeight:900, fontSize:'22px' }}>TE</span></div>
        </div>
      </div>

      {/* Status text */}
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.35)', marginBottom: 6 }}>
          Tiger Eye
        </div>
        <div style={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.55)', fontWeight: 500, display: 'flex', alignItems: 'center', gap: 6 }}>
          {message}
          <span style={{ display: 'flex', gap: 3, marginLeft: 2 }}>
            {[0,1,2].map(i => (
              <span key={i} style={{ width: 4, height: 4, borderRadius: '50%', background: '#824AF8', display: 'inline-block', animation: `loader-dot 1.4s ease-in-out ${i * 0.2}s infinite` }} />
            ))}
          </span>
        </div>
      </div>

      {/* Progress bar */}
      <div style={{ width: 120, height: 2, background: 'rgba(255,255,255,0.08)', borderRadius: 2, overflow: 'hidden' }}>
        <div style={{ height: '100%', width: '30%', background: 'linear-gradient(90deg,#5899CD,#824AF8,#F37664)', animation: 'pulse-slide 1.6s ease-in-out infinite' }} />
      </div>
    </div>
  </div>
);

// Stable wrapper — must be defined outside AppRouter so React never remounts
// the provider tree on AuthContext re-renders (session refresh, profile fetch, etc.)
const InternalApp = React.memo(({ isMobile }) => (
  <RealtimeProvider>
  <AppDataProvider>
  <SyncProvider>
    <Router>
      <div className="App" style={{ height: '100vh', width: '100vw', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
  <ErrorBoundary title="Command Center Error">
    <Suspense fallback={<AppLoader />}>
      <Routes>
            <Route path="/"               element={isMobile ? <MobileMap /> : <CommandCenter />} />
            <Route path="/mobile"          element={<MobileMap />} />
            <Route path="/command-center" element={isMobile ? <Navigate to="/" replace /> : <CommandCenter />} />
            <Route path="/client/*"       element={<Navigate to="/" replace />} />
            <Route path="*"               element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
  </ErrorBoundary>
      </div>
    </Router>
  </SyncProvider>
  </AppDataProvider>
  </RealtimeProvider>
));

// Stable wrapper for the internal MOBILE experience — mirrors InternalApp so the
// Realtime/AppData/Sync providers MobileMap depends on are mounted once and never
// remount on AuthContext re-renders. One screen (MobileShell -> MobileMap); Map vs
// Operational Metrics is a tab MobileMap owns internally (RMI-2026-429).
const MobileApp = React.memo(() => (
  <RealtimeProvider>
  <AppDataProvider>
  <SyncProvider>
    <ErrorBoundary title="Mobile Error">
      <Suspense fallback={<AppLoader />}>
        <MobileShell />
      </Suspense>
    </ErrorBoundary>
  </SyncProvider>
  </AppDataProvider>
  </RealtimeProvider>
));

// RfpFullScreen (the shared RFP shell) now lives in src/components/RFP/ and is
// used by both the standalone landing (below) and the Admin Dashboard panel.

const initialHash = window.location.hash;
const isInitialRecovery = initialHash.includes('type=recovery') || initialHash.includes('type=invite');

// ── Mobile/tablet detection — imported from shared hook ─────────────────────
// useIsMobile() is a pure viewport-width check (< 768px, MOBILE_BREAKPOINT),
// which classifies most tablets in portrait as "mobile" too. For internal
// staff, isTabletDevice() (UA/touch-based) is used alongside it so tier-4+
// tablet users land on the desktop Command Center instead of MobileShell,
// which has no Reports feature -- see the RMI-2026-379 comment at the
// internal-staff routing matrix below for the tier<4/field-tech exemption.
// URL param ?mobile=1 still forces mobile view regardless of screen size.

const AppRouter = () => {
  const { session, profile, isAuthLoading, logout } = useAuth();
  const isMobile = useIsMobile();
  const [isRecoveryMode, setIsRecoveryMode] = useState(isInitialRecovery);
  // RMI-2026-591: forceUnlock is read below to bypass the auth loader, but
  // setForceUnlock is never called anywhere -- this escape hatch (for a
  // stuck "Initializing secure session..." screen) can currently never be
  // triggered. Flagged as a product gap, not deleted; renamed with the
  // `_`-prefix convention rather than inventing a trigger (e.g. a "stuck?
  // tap here" button) in what's meant to be a lint pass.
  const [forceUnlock, _setForceUnlock] = useState(false);
  // Tracks when the user has clicked Google/email login but profile hasn't arrived yet.
  // Prevents TigerGate from re-rendering during the auth resolution window.
  const [isAuthenticating, setIsAuthenticating] = useState(
    // Pre-set true on OAuth callback so the loader shows immediately on redirect-back
    window.location.hash.includes('access_token') || window.location.hash.includes('type=signup')
  );

  // Reset isAuthenticating whenever session is cleared (logout, token expiry).
  // Without this, the loader stays visible forever after signing out.
  // RMI-2026-591 (react-hooks/set-state-in-effect): defer the setState one
  // tick out of the effect's own synchronous execution.
  useEffect(() => {
    if (!session) {
      const t = setTimeout(() => setIsAuthenticating(false), 0);
      return () => clearTimeout(t);
    }
  }, [session]);

  // CHUNK PRELOAD: kick off the post-auth route chunk download the moment
  // we have a session, in parallel with the AuthContext profile fetch.
  // We dispatch on session alone (before profile resolves) for the mobile/
  // internal-staff path since those chunks are large. For clients we wait
  // until profile is known. Either way this overlaps the JS download with
  // the auth bootstrap rather than serializing them.
  useEffect(() => {
    if (!session) return;
    // If profile not yet known, optimistically preload the most likely chunk
    // (mobile or desktop staff). Client preload waits until profile_type is known.
    if (!profile) {
      preloadPostAuthChunks(isMobile, null);
      return;
    }
    preloadPostAuthChunks(isMobile, profile.profile_type);
  }, [session, profile, isMobile]);

  // Escape hatch: if session exists but profile never arrives (e.g. slow mobile
  // connection, Supabase timeout, or null profile_type falling through), stop
  // showing the loader after 3.5s and fall through to TigerGate so the user
  // isn't permanently stuck. Tightened from 6s — combined with the 2.5s
  // failsafe in AuthContext, total worst-case loader time is now ~3.5s
  // instead of ~11s.
  const [profileWaitExpired, setProfileWaitExpired] = useState(false);
  // RMI-2026-591 (react-hooks/set-state-in-effect): defer the reset branch's
  // setState one tick out of the effect's own synchronous execution; the
  // 3.5s failsafe branch below was already async and unaffected.
  useEffect(() => {
    if (!session || profile) {
      const resetTimer = setTimeout(() => setProfileWaitExpired(false), 0);
      return () => clearTimeout(resetTimer);
    }
    const t = setTimeout(() => setProfileWaitExpired(true), 3500);
    return () => clearTimeout(t);
  }, [session, profile]);

  const tosAppKey = (typeof window !== 'undefined' && window.location.hostname === 'rfp-pulse.tigertechlogis.com') ? 'rfp-pulse' : 'eye';
  const tos = useTosGate(session, profile, tosAppKey);

  // RMI-2026-591 (react-hooks/immutability): these two window.location.href
  // assignments used to happen directly in the render body below --
  // mutating browser navigation state during render is impure. Moved into
  // effects; the render body still returns its loader/redirect screen
  // synchronously (unchanged UX), the actual navigation just now fires a
  // tick later via the effect instead of during render itself.
  useEffect(() => {
    if (!profile || typeof window === 'undefined') return;
    const onAccountingDomain = window.location.hostname === 'accounting.tigertechlogis.com';
    const onAccountingPath = window.location.pathname.startsWith('/accounting');
    if (!onAccountingDomain && !onAccountingPath) return;
    const qualifies = profile.profile_type === 'internal' && (profile.tier_level || 0) >= 8;
    if (!qualifies) window.location.href = onAccountingDomain ? 'https://eye.tigertechlogis.com' : '/';
  }, [profile]);

  useEffect(() => {
    if (!profile || typeof window === 'undefined') return;
    if (profile.profile_type === 'org_user') window.location.href = 'https://rfp-pulse.tigertechlogis.com';
  }, [profile]);

  if (isAuthLoading && !forceUnlock) return <AppLoader message="Initializing secure session..." />;

  // Session resolved but profile still loading — show loader, not TigerGate.
  // profileWaitExpired breaks the loop if profile never arrives after 3.5s.
  if (session && !profile && !profileWaitExpired) return <AppLoader message="Loading your profile..." />;

  // Only block on authenticating if we still have a live session — on logout
  // session is null so this correctly falls through to TigerGate.
  if (isAuthenticating && session && !profile) return <AppLoader message="Authenticating..." />;

  if (isRecoveryMode) return <SetupPassword onComplete={() => setIsRecoveryMode(false)} />;
  if (!session || !profile) return <TigerGate forceRecoveryMode={isRecoveryMode} onAuthenticating={() => setIsAuthenticating(true)} />;

  // ── Terms of Service gate (RMI-2026-256): must accept the current published ToS before any routing.
  if (tos.loading) return <AppLoader message="Checking terms of service..." />;
  if (!tos.accepted) return <TosGate doc={tos.doc} onAccepted={tos.refresh} onLogout={logout} />;

  // ── 0. RFP PULSE DOMAIN ─────────────────────────────────────────────────
  // RMI-2026-253 (BUILD-2026.496): when the app is served from the RFP Pulse
  // hostname, every authenticated user lands in the standalone RFP screen —
  // the domain is the product. Checked before client/internal branching; the
  // full console stays on eye.tigertechlogis.com.
  if (typeof window !== 'undefined' && window.location.hostname === 'rfp-pulse.tigertechlogis.com') {
    return (
      <ErrorBoundary title="RFP Error">
        <Suspense fallback={<AppLoader message="Loading opportunities..." />}>
          <RfpFullScreen profile={profile} mode="logout" onLogout={logout} />
        </Suspense>
      </ErrorBoundary>
    );
  }

  // ── 0.5 ACCOUNTING MODULE — DOMAIN + PATH ────────────────────────────────
  // RMI-2026-512/513: standalone internal accounting interface, same
  // full-screen-shell pattern as RFP Pulse above. Now reachable two ways:
  // the accounting.tigertechlogis.com subdomain (added once the module was
  // ready to carry its own DNS identity, ahead of an eventual fully
  // independent domain of its own) and the original /accounting path on
  // the Eye host (kept working for any existing internal links/bookmarks).
  // Tier-8 gated (see SCHEMA.md "GL — Internal Accounting Module");
  // non-qualifying profiles are bounced by the effect above -- this render
  // body only returns the loader/screen synchronously, same reasoning as
  // the RMI-2026-591 comment on that effect (no navigation during render).
  const onAccountingDomain = typeof window !== 'undefined' && window.location.hostname === 'accounting.tigertechlogis.com';
  const onAccountingPath = typeof window !== 'undefined' && window.location.pathname.startsWith('/accounting');
  if (onAccountingDomain || onAccountingPath) {
    const qualifies = profile.profile_type === 'internal' && (profile.tier_level || 0) >= 8;
    if (!qualifies) {
      return <AppLoader message="Redirecting..." />;
    }
    return (
      <ErrorBoundary title="Accounting Error">
        <Suspense fallback={<AppLoader message="Loading Accounting..." />}>
          <AccountingApp profile={profile} onClose={() => { window.location.href = onAccountingDomain ? 'https://eye.tigertechlogis.com' : '/'; }} />
        </Suspense>
      </ErrorBoundary>
    );
  }

  // ── 1. CLIENT ROUTING MATRIX ────────────────────────────────────────────
  // org_user (external SaaS org member) belongs in RFP Pulse; if one reaches the
  // Eye host, redirect. On the rfp-pulse host the guard above already handled them.
  if (profile.profile_type === 'org_user') {
    return <AppLoader message="Redirecting to RFP Pulse..." />;
  }

  if (profile.profile_type === 'client') {

    // Select desktop or mobile screen per route
    const Overview  = isMobile ? MobileClientOverview   : ClientOverview;
    const Intake    = isMobile ? MobileClientIntakeForm  : ClientIntakeForm;
    const CMDB      = isMobile ? MobileClientCMDB        : ClientCMDB;
    const Messages  = isMobile ? MobileClientMessages    : ClientMessages;
    const Profile   = ClientProfileEditor;

    return (
      <Router>
        <Routes>
          <Route path="/client" element={<ErrorBoundary title="Client Dashboard Error"><Suspense fallback={<AppLoader />}><ClientDashboardLayout /></Suspense></ErrorBoundary>}>
            <Route index                  element={<Navigate to="dashboard" replace />} />
            <Route path="dashboard"       element={<ErrorBoundary title="Client Overview Error"><Suspense fallback={<AppLoader />}><Overview /></Suspense></ErrorBoundary>} />
            <Route path="request"         element={<ErrorBoundary title="Client Intake Error"><Suspense fallback={<AppLoader />}><Intake /></Suspense></ErrorBoundary>} />
            <Route path="cmdb"            element={<ErrorBoundary title="Client CMDB Error"><Suspense fallback={<AppLoader />}><CMDB /></Suspense></ErrorBoundary>} />
            <Route path="initiatives"     element={<ErrorBoundary title="Client Initiatives Error"><Suspense fallback={<AppLoader />}><ClientInitiatives /></Suspense></ErrorBoundary>} />
            <Route path="messages"        element={<ErrorBoundary title="Client Messages Error"><Suspense fallback={<AppLoader />}><Messages /></Suspense></ErrorBoundary>} />
            <Route path="profile"         element={<ErrorBoundary title="Client Profile Error"><Suspense fallback={<AppLoader />}><Profile /></Suspense></ErrorBoundary>} />
            <Route path="*"               element={<Navigate to="dashboard" replace />} />
          </Route>
          <Route path="/"  element={<Navigate to="/client/dashboard" replace />} />
          {/* RMI-2026-238 (BUILD-2026.475): redirect any stray path to the client
              dashboard instead of a dead-end diagnostic, so the client PWA
              self-heals when launched at e.g. /index.html (stale install /
              home-screen shortcut). Mirrors InternalApp's catch-all. */}
          <Route path="*" element={<Navigate to="/client/dashboard" replace />} />
        </Routes>
      </Router>
    );
  }

  // ── 2. INTERNAL STAFF ROUTING MATRIX ────────────────────────────────────
  if (profile.profile_type === 'internal') {
    // RMI-2026-247: users whose landing_view === 'rfp' are locked to the
    // standalone RFP screen and see nothing else. Checked before the tier gate
    // so a dedicated business-development user always lands here on login.
    if (profile.landing_view === 'rfp') {
      return (
        <ErrorBoundary title="RFP Error">
          <Suspense fallback={<AppLoader message="Loading opportunities..." />}>
            <RfpFullScreen profile={profile} mode="logout" onLogout={logout} />
          </Suspense>
        </ErrorBoundary>
      );
    }
    // Mobile access is NOT tier-gated (RMI-2026-308); the desktop Command Center
    // still requires tier 4+. profile.mobile_home is read directly by MobileMap
    // itself (RMI-2026-429) to seed which tab (Map vs Operational Metrics) a user
    // lands on by default -- no longer computed or threaded through here.
    // RMI-2026-379: the mobile app has no Reports feature at all, so
    // a tablet under the 768px breakpoint (common in portrait) silently lost
    // Reports/Favorites entirely. Route tier-4+ tablet users to the desktop
    // Command Center instead. Deliberately does NOT touch phones or tier<4
    // field techs -- the mobile_home bypass above must stay intact for them
    // regardless of device -- and an explicit ?mobile= param still wins.
    const mobileParamOverride = typeof window !== 'undefined' && new URLSearchParams(window.location.search).has('mobile');
    const routeAsMobile = isMobile && !(!mobileParamOverride && isTabletDevice() && profile.tier_level >= 4);
    if (!routeAsMobile && profile.tier_level < 4) {
      return (
        <div className="access-denied-screen" style={{ color: '#fff', textAlign: 'center', paddingTop: '30vh', background: '#231F20', height: '100vh', width: '100vw', fontFamily: 'Poppins, sans-serif' }}>
          <h2 style={{ fontSize: '2rem', color: '#F37664' }}>Access Restricted</h2>
          <p style={{ opacity: 0.6, marginTop: '10px' }}>Security Clearance Tier 4+ required for TECC Command Center access.</p>
          <button onClick={logout} style={{ marginTop: '30px', padding: '12px 24px', background: 'linear-gradient(135deg,#5899CD 0%,#824AF8 55%,#F37664 100%)', border: 'none', borderRadius: '6px', color: '#fff', fontWeight: '600', cursor: 'pointer' }}>Return to Gate</button>
        </div>
      );
    }

    if (routeAsMobile) {
      return <MobileApp />;
    }

    return <InternalApp isMobile={false} />;
  }

  return (
    <div style={{ color: 'red', textAlign: 'center', marginTop: '20vh', fontFamily: 'monospace' }}>
      CRITICAL ERROR: Corrupted Profile Matrix. Contact Tier 9 Administrator.
    </div>
  );
};

export default function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
          <AppRouter />
      </AuthProvider>
    </ThemeProvider>
  );
}
/*
=========================================
CHANGE MANIFEST
=========================================
VER 4.14 - BUILD-2026.729 - August 7, 2026 — RMI-2026-429: fold MobileCRM into one mobile interface
- MobileApp no longer computes or threads hasMap/hasOffice -- MobileCRM.jsx
  (the former "Console" screen) is deleted this build, so there's only ever
  one mobile screen to mount. profile.mobile_home is read directly by
  MobileMap itself to seed its own Map/Operational-Metrics tab default.
  MobileApp and MobileShell both dropped their profile/onLogout/hasMap/
  hasOffice props accordingly -- MobileMap already self-fetches profile/
  logout via useAuth().

VER 4.13 - BUILD-2026.685 - July 30, 2026 — RMI-2026-379: tablets get Reports/Favorites
- FIX: Reports (and Favorites) don't exist anywhere in the Mobile app
  (MobileShell's map/office modes are MobileMap/MobileCRM, nothing else), so
  any tablet under the 768px width breakpoint -- common in portrait -- was
  silently routed there with no way to reach Reports at all.
- Added isTabletDevice() (src/hooks/useIsMobile.js) and used it, alongside
  profile.tier_level >= 4, to route tier-4+ tablet users to the desktop
  Command Center instead. Tier<4 field techs and all phone users are
  unaffected (the mobile_home bypass stays intact for them regardless of
  device); an explicit ?mobile= param still wins over the tablet check.
=========================================
VER 4.12 - BUILD-2026.500 - July 2, 2026 — RMI-2026-258: org_user routing
- An external org member (profile_type 'org_user') who lands on the Eye host is
  redirected to rfp-pulse (on the rfp-pulse host the domain guard already sends
  them to the standalone RFP screen). Keeps external users out of Eye routing.
VER 4.11 - BUILD-2026.499 - July 2, 2026 — RMI-2026-257: per-app ToS
- The ToS gate is now per application: appKey is resolved from the hostname
  ('rfp-pulse' vs 'eye') and passed to useTosGate, so each app has its own terms,
  version sequence, and acceptance.
VER 4.10 - BUILD-2026.498 - July 2, 2026 — RMI-2026-256: Terms-of-Service acceptance gate
- FEATURE: after the TigerGate auth guard (before the RFP-Pulse/client/internal branches),
  useTosGate checks whether the user accepted the current published ToS; if not, TosGate is
  rendered full-screen and nothing else is reachable until they accept. Applies to every app.
VER 4.9 - BUILD-2026.496 - July 2, 2026 — RMI-2026-253: RFP Pulse domain routing
- FEATURE: when window.location.hostname === 'rfp-pulse.tigertechlogis.com', every
  authenticated user renders the standalone RFP screen (RfpFullScreen mode=logout),
  checked before the client/internal branches. The rfp-pulse domain is the RFP Pulse
  product entry; the full console remains on eye.tigertechlogis.com. Reuses the same
  shell/import as the landing_view === 'rfp' path.
VER 4.8 - BUILD-2026.490 - July 2, 2026 — RMI-2026-247: shared RFP full-screen shell
- REFACTOR: extracted the RFP shell to src/components/RFP/RfpFullScreen.jsx so the
  standalone landing and the Admin Dashboard "Gov RFPs" entry render the identical
  full-viewport interface. Admin now opens it as a full-screen overlay (mode=close,
  X back to the dashboard) instead of a boxed 78vh panel. Standalone uses mode=logout.
- Removed the old inline RfpStandalone; the lazy import now targets RfpFullScreen.

VER 4.7 - BUILD-2026.485 - July 1, 2026 — RMI-2026-247 RFP LANDING VIEW
- FEATURE: internal users whose profiles_v2.landing_view === 'rfp' now render a
  new standalone locked layout (RfpStandalone) — the Government RFP screen plus a
  logout control, nothing else. The intercept sits at the top of the internal
  routing branch, before the desktop tier-4 gate, so a dedicated BD user always
  lands on the RFP feed. 'dashboard' and 'both' users are unaffected (they reach
  the RFP screen from the Admin Dashboard's new "Gov RFPs" panel instead).
- Added a lazy import for RfpOpportunities (src/components/RFP/). RfpStandalone
  needs no Realtime/Sync/AppData providers — the component uses only the Supabase
  client — so the shell stays minimal.
=========================================
VER 4.6 - BUILD-2026.475 - June 24, 2026 — RMI-2026-238 CLIENT PWA DEAD-END FIX
- BUGFIX: Client routing matrix top-level catch-all (<Route path="*">) was
  rendering a leftover debug diagnostic ("ROUTING DIAGNOSTIC: Dead end at
  {pathname}") instead of redirecting. A client-profile session launched at any
  path other than '/' or '/client/...' (notably /index.html from a stale
  installed PWA / home-screen shortcut on tablets) fell through to it and saw a
  dead-end screen. Replaced with <Navigate to="/client/dashboard" replace />,
  matching InternalApp and the nested /client/* catch-all. Client PWA now
  self-heals on any launch path. One route element swapped; no logic/state change.
=========================================
VER 4.5 - May 14, 2026 — LOGIN LATENCY REDUCTION
- PERF: Added chunk preload effect. The moment a session exists, the post-auth
  route chunk (CommandCenter / MobileMap / ClientDashboardLayout) starts
  downloading in parallel with the profile fetch. Previously the chunk
  download didn't start until AFTER profile resolved and AppRouter chose
  a route — adding the full JS download time to perceived login latency.
- PERF: profileWaitExpired timeout reduced from 6000ms to 3500ms. Paired
  with the AuthContext failsafe drop to 2500ms, total worst-case stuck-loader
  time is now ~3.5s instead of the previous ~11s.
- REFACTOR: CommandCenter, MobileMap, and ClientDashboardLayout lazy factories
  extracted as named consts (loadCommandCenter etc.) so they can be invoked
  directly for preload without going through React.lazy machinery.
=========================================
VER 4.4 - May 13, 2026
- BUGFIX: Added profileWaitExpired escape hatch (6s timeout) so that if profile
  never arrives (fetch failed silently, no profiles_v2 row, etc.) the loader
  breaks out and falls through to TigerGate instead of hanging forever.
=========================================
VER 4.3 - May 13, 2026
- BUGFIX: isAuthenticating guard now requires session to be truthy.
  `isAuthenticating && !profile` was firing during logout when session
  was null but isAuthenticating hadn't cleared yet (useEffect runs after
  render), leaving the loader stuck. Added `&& session` to the condition.
=========================================
VER 4.2 - May 13, 2026
- BUGFIX: isAuthenticating now resets via useEffect when session goes null.
  Previously logout left the loader visible indefinitely.
=========================================
VER 4.1 - May 13, 2026
- FEATURE: AppLoader replaced with full branded auth screen (logo ring pulse,
  ambient blobs, animated dots, gradient progress bar, status message prop).
- BUGFIX: AppRouter now shows AppLoader when session exists but profile is null,
  preventing TigerGate flash during post-login profile fetch window.
- BUGFIX: isAuthenticating state pre-set true on OAuth callback hash detection
  so loader shows immediately when Google redirects back to the app.
- AppRouter passes onAuthenticating() to TigerGate; fired on Google + email
  login to swap to loader before auth event propagates.
=========================================
VER 3.9 - Lazy imports + AppLoader
VER 3.8 - RealtimeProvider Integration
VER 3.7 - Mobile client routing
VER 3.6 - Auth stability
=========================================
*/
/* [ END OF MODULE - VER 4.14 (src/App.jsx) ] */
